import type { Control } from "../core/Control.js";
import type { Size } from "../../common/geometry.js";
import { resolveTracks } from "./Grid.js";
import { LayoutPanel, type LayoutPanelOptions } from "./LayoutPanel.js";

/**
 * How a child divides up the space along the stacking axis.
 *
 * `fill` is a weight, read the way a grid reads `"2*"`: two children with weights 2 and 1 split
 * the leftovers two-to-one. `fraction` is a share of the whole axis, `0.5` for half of it. A
 * child with neither keeps the size it has, which is the default and what most children want.
 *
 * Kept beside the panel rather than on the control, exactly as `GridPlacement` is: a weight only
 * means anything relative to one panel's main axis, and putting `fill` on `Control` would spread
 * one layout's vocabulary across every control in the library.
 */
export interface StackPlacement {
  /** Share of the leftover space, by weight. `"*"` is 1, `"2*"` is 2. */
  fill?: number;
  /** Share of the whole axis, 0 to 1. */
  fraction?: number;
}

export interface StackPanelOptions extends LayoutPanelOptions {
  /** Size the last child to exactly the space left over on the main axis. */
  fillLast?: boolean;
}

interface Entry {
  child: Control;
  main: number;
  mainBefore: number;
  mainAfter: number;
  crossBefore: number;
  crossAfter: number;
  cross: number;
  /** Weight if this child shares the leftovers, else 0. */
  weight: number;
}

/**
 * Lays children out in a single line, vertically by default.
 *
 * Each child keeps its own size along the stacking axis unless it asks for a share of the space
 * — `add(child, { fill: 1 })` — and `align` decides what happens across it, defaulting to
 * `"stretch"`, the same default WPF gets from `HorizontalAlignment.Stretch`.
 *
 * ```ts
 * const side = new StackPanel({ padding: insets(8), spacing: 6 });
 * side.controls.add(new Button({ height: 30, text: "Save" }));
 * side.add(new ListBox(), { fill: 1 });   // takes whatever is left
 * side.controls.add(new Button({ height: 30, text: "Cancel" }));
 * ```
 */
export class StackPanel extends LayoutPanel {
  /** Give the last visible child exactly the main-axis space left over. */
  fillLast: boolean;

  #placement = new WeakMap<Control, StackPlacement>();

  constructor(options: StackPanelOptions = {}) {
    super(options);
    this.fillLast = options.fillLast ?? false;
    this.performLayout();
  }

  /** Add a child, saying how it divides up the stacking axis. */
  add<T extends Control>(control: T, placement?: StackPlacement): T {
    if (placement) this.#placement.set(control, placement);
    this.controls.add(control);
    return control;
  }

  /** Change how an existing child divides up the stacking axis. */
  setPlacement(control: Control, placement: StackPlacement): void {
    this.#placement.set(control, placement);
    this.performLayout();
    this.invalidate();
  }

  placementOf(control: Control): StackPlacement {
    return this.#placement.get(control) ?? {};
  }

  /**
   * What each child wants along both axes, before anything is handed out.
   *
   * `weight` is non-zero for the children that share the leftovers; their `main` is what they
   * would take if nothing were left, which is the honest answer for a measure and zero in a
   * layout that has run out of room.
   */
  #entries(children: readonly Control[], available: Size): Entry[] {
    const horizontal = this.horizontal;
    const mainAvailable = horizontal ? available.width : available.height;
    const crossAvailable = horizontal ? available.height : available.width;
    // `fillLast` is the old spelling of a weight on the last child, and stays exactly that:
    // an explicit weight anywhere turns it off, because the layout has said what it wants.
    const explicit = children.some((c) => {
      const p = this.placementOf(c);
      return p.fill !== undefined || p.fraction !== undefined;
    });
    const lastIndex = children.length - 1;

    return children.map((child, i) => {
      const m = this.marginOf(child);
      const p = this.placementOf(child);
      const offer = horizontal
        ? { width: mainAvailable, height: Math.max(0, crossAvailable - m.crossBefore - m.crossAfter) }
        : { width: Math.max(0, crossAvailable - m.crossBefore - m.crossAfter), height: mainAvailable };
      const natural = this.naturalSize(child, offer);

      let weight = 0;
      let main = horizontal ? natural.width : natural.height;
      if (p.fill !== undefined) weight = Math.max(0, p.fill);
      else if (p.fraction !== undefined) main = Math.max(0, p.fraction) * mainAvailable;
      else if (!explicit && this.fillLast && i === lastIndex) weight = 1;
      if (weight > 0) main = 0;

      return {
        child,
        main,
        cross: horizontal ? natural.height : natural.width,
        weight,
        ...m,
      };
    });
  }

  /** Hand the leftovers to the weighted children, a pixel-exact share each. */
  #distribute(entries: Entry[], mainAvailable: number): void {
    const weighted = entries.filter((e) => e.weight > 0);
    if (weighted.length === 0) return;

    let taken = 0;
    for (const e of entries) taken += e.mainBefore + e.main + e.mainAfter;
    const gaps = this.spacing * Math.max(0, entries.length - 1);
    const spare = Math.max(0, mainAvailable - taken - gaps);

    // The grid's arithmetic, so one star column and one filled stack child round the same way.
    const shares = resolveTracks(
      weighted.map((e) => `${e.weight}*`),
      spare,
      0,
      () => 0,
    );
    weighted.forEach((e, i) => {
      e.main = shares[i] ?? 0;
    });
  }

  protected override arrange(available: Size): Size {
    const children = this.laidOutChildren();
    if (children.length === 0) return { width: 0, height: 0 };

    const horizontal = this.horizontal;
    const mainAvailable = horizontal ? available.width : available.height;
    const crossAvailable = horizontal ? available.height : available.width;

    const entries = this.#entries(children, available);
    this.#distribute(entries, mainAvailable);

    let offset = 0;
    let crossExtent = 0;
    for (const e of entries) {
      const cross = this.crossPlace(
        crossAvailable,
        e.cross,
        e.crossBefore,
        e.crossAfter,
        this.crossAlignOf(e.child),
      );

      const mainStart = offset + e.mainBefore;
      if (horizontal) e.child.setBounds(mainStart, cross.offset, e.main, cross.size);
      else e.child.setBounds(cross.offset, mainStart, cross.size, e.main);

      // The next child starts past this one's far margin, which is what stops a picture with a
      // margin being written over by the paragraph after it.
      offset += e.mainBefore + e.main + e.mainAfter + this.spacing;
      crossExtent = Math.max(crossExtent, cross.offset + cross.size + e.crossAfter);
    }

    const mainExtent = Math.max(0, offset - this.spacing);
    return horizontal
      ? { width: mainExtent, height: crossExtent }
      : { width: crossExtent, height: mainExtent };
  }

  /**
   * What the children need, with the weighted ones asking for nothing.
   *
   * A stack measured by its own parent is being asked how small it could be; a child that
   * volunteered to absorb slack has no opinion about that, and counting its current size would
   * make the answer depend on the last pass rather than on the content.
   */
  protected override measure(available: Size): Size {
    const children = this.laidOutChildren();
    if (children.length === 0) return { width: 0, height: 0 };

    const entries = this.#entries(children, available);
    let main = 0;
    let cross = 0;
    for (const e of entries) {
      main += e.mainBefore + e.main + e.mainAfter;
      cross = Math.max(cross, e.crossBefore + e.cross + e.crossAfter);
    }
    main += this.spacing * Math.max(0, entries.length - 1);

    return this.horizontal ? { width: main, height: cross } : { width: cross, height: main };
  }

  /** Auto-sizing a stack grows it along the axis it stacks on. */
  protected override applyAutoSize(content: Size): void {
    const pad = this.padding;
    if (this.horizontal) this.setSize(content.width + pad.left + pad.right, this.height);
    else this.setSize(this.width, content.height + pad.top + pad.bottom);
  }
}
